import { Box, Button, Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo } from "react";
import { RHFRadioGroup } from "../../hook-form/RHFRadioGroup";
import { RHFTextField } from "../../hook-form/RHFTextField";
import { RHFDatePicker } from "../../hook-form/RHFDateSelect";
import { RHFCheckbox } from "../../hook-form/RHFCheckbox";
import {
  GlassLoaderPostPayload,
  GlassType,
} from "../../../types/GlassLoaderTypes";
import { postGlassType } from "../../../services/glassLoaderService";

type Props = {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  glassTypes?: GlassType[];
  stationId: number;
};

export function GlassLoaderForm({
  onSuccess,
  onError,
  glassTypes = [],
  stationId,
}: Props) {
  const methods = useForm<{
    glassBatch: string;
    glassType: string;
    customGlassType: string;
    ishardeningpass: boolean;
    ispaletteopened: number;
    productionDate?: Date | null;
  }>({
    defaultValues: {
      glassBatch: "",
      glassType: "",
      customGlassType: "",
      ishardeningpass: false,
      ispaletteopened: 0,
      productionDate: null,
    },
  });

  const { control, handleSubmit, reset, setError, watch } = methods;
  const watchGlassType = watch("glassType");

  const onSubmit = async (data: {
    glassBatch: string;
    glassType: string;
    productionDate?: Date | null;
    customGlassType: string;
    ishardeningpass: boolean;
    ispaletteopened: number;
  }) => {
    let displayName = data.glassType;
    if (displayName === "__OTHER__") {
      const trimmed = data.customGlassType.trim();
      if (!trimmed) {
        setError("customGlassType", {
          type: "required",
          message: "Bitte benutzerdefinierten Glastyp eingeben",
        });
        return;
      }
      displayName = trimmed;
    }

    const chosenGlassType = glassTypes.find((g) => g.name === displayName);
    const resolvedStandardGlass =
      (chosenGlassType && chosenGlassType.standardGlass) != null
        ? chosenGlassType!.standardGlass
        : stationId;

    const isPaletteOpenedBool = Number(data.ispaletteopened) === 1;

    const payload: GlassLoaderPostPayload = {
      stationid: stationId,
      productiondate: data.productionDate || undefined,
      dateInProduction: new Date(),
      glassbatch: data.glassBatch,
      glasstype: stationId === 54 ? "frontglass" : "backglass",
      ishardeningpass: data.ishardeningpass,
      ispaletteopened: isPaletteOpenedBool,
      glassColor: undefined,
      name: displayName,
      standardglass: resolvedStandardGlass,
    };

    try {
      await postGlassType(payload);
      reset();
      onSuccess?.();
    } catch (err: any) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        console.debug("GlassLoader request canceled");
        return;
      }
      onError?.(err?.message || "Fehler beim Speichern");
    }
  };

  const glassTypeOptions = useMemo(() => {
    const base = glassTypes.map((t) => ({ label: t.name, value: t.name }));
    const filtered = base.filter(
      (o) => (o.label?.toLowerCase() || "") !== "andere"
    );
    filtered.push({ label: "andere", value: "__OTHER__" });
    return filtered;
  }, [glassTypes]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <RHFTextField
            name="glassBatch"
            label="Glas Batch"
            control={control}
            required
          />
          <RHFDatePicker
            name="productionDate"
            label="Glas Produktionsdatum"
            control={control}
          />
          <RHFCheckbox
            name="ishardeningpass"
            label="Glas ist gehärtet (bitte zuerst testen)"
            control={control}
          />
          <RHFRadioGroup
            name="ispaletteopened"
            label="Ist die Palette bereits geöffnet?"
            control={control}
            required
            options={[
              { label: "Ja", value: 1 },
              { label: "Nein", value: 0 },
            ]}
          />
          <RHFRadioGroup
            name="glassType"
            label="Glastyp"
            control={control}
            required
            options={glassTypeOptions}
          />
          {watchGlassType === "__OTHER__" && (
            <RHFTextField
              name="customGlassType"
              label="Benutzerdefinierter Glastyp"
              control={control}
              required
            />
          )}
          <Button type="submit" variant="contained" color="primary">
            Speichern
          </Button>
        </Stack>
      </Box>
    </FormProvider>
  );
}
